import React, { useState, useEffect, useCallback } from "react";
import { useMainCompany } from "../../context/MainCompanyContext";
import api from "../../api/axios";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import Toast from "../../components/common/Toast";
import { Save, FileText, Calculator, Printer } from "lucide-react";
import { Editor } from "@tinymce/tinymce-react";
import { DASHBOARD_OPTION_LABELS } from "../../utils/dashboardOptions";

/**
 * Calculator keys used in API (must match backend).
 * Optional calculator for GET /api/auth/disclaimer: buyer | seller | refinance | seller2net | propnine | quick | open.
 */
export const DISCLAIMER_CALCULATOR_KEYS = [
  "buyer",
  "seller",
  "refinance",
  "seller2net",
  "propnine",
];

const CALC_LABELS = {
  buyer: DASHBOARD_OPTION_LABELS.buyer ?? "Buyer Net Sheet",
  seller: DASHBOARD_OPTION_LABELS.seller ?? "Seller Net Sheet",
  refinance: DASHBOARD_OPTION_LABELS.refinance ?? "Refinance",
  seller2net: DASHBOARD_OPTION_LABELS.seller2net ?? "Seller 2 Net",
  propnine: DASHBOARD_OPTION_LABELS.propnine ?? "ProPosition 19",
};

export default function AdminManageDisclaimer() {
  const { selectedMain, mainCompanies } = useMainCompany();
  const [defaultDisclaimer, setDefaultDisclaimer] = useState("");
  const [calculatorDisclaimers, setCalculatorDisclaimers] = useState({});
  const [defaultPrintDisclaimer, setDefaultPrintDisclaimer] = useState("");
  const [calculatorPrintDisclaimers, setCalculatorPrintDisclaimers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
  const [activeCalc, setActiveCalc] = useState(null);
  const [activePrintCalc, setActivePrintCalc] = useState(null);

  const selectedCompany = mainCompanies?.find((m) => m.id === selectedMain);

  const showToast = useCallback((message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  const fetchDisclaimer = useCallback(async () => {
    if (!selectedMain) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/disclaimer", {
        params: { maincompanyid: selectedMain },
      });
      const raw = data?.data ?? data;
      const defaultText = raw?.defaultDisclaimer ?? "";
      const calcTexts = raw?.calculatorDisclaimers ?? {};
      const defaultPrintText = raw?.defaultPrintDisclaimer ?? "";
      const calcPrintTexts = raw?.calculatorPrintDisclaimers ?? {};
      setDefaultDisclaimer(typeof defaultText === "string" ? defaultText : "");
      setCalculatorDisclaimers(calcTexts && typeof calcTexts === "object" ? calcTexts : {});
      setDefaultPrintDisclaimer(typeof defaultPrintText === "string" ? defaultPrintText : "");
      setCalculatorPrintDisclaimers(
        calcPrintTexts && typeof calcPrintTexts === "object" ? calcPrintTexts : {}
      );
    } catch (err) {
      console.error("fetchDisclaimer error:", err);
      showToast(err?.response?.data?.message || "Failed to load disclaimer", "error");
      setDefaultDisclaimer("");
      setCalculatorDisclaimers({});
      setDefaultPrintDisclaimer("");
      setCalculatorPrintDisclaimers({});
    } finally {
      setLoading(false);
    }
  }, [selectedMain, showToast]);

  useEffect(() => {
    fetchDisclaimer();
  }, [fetchDisclaimer]);

  const setCalculatorDisclaimer = (key, value) => {
    setCalculatorDisclaimers((prev) => ({ ...prev, [key]: value }));
  };

  const setCalculatorPrintDisclaimer = (key, value) => {
    setCalculatorPrintDisclaimers((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selectedMain) {
      showToast("Please select a main company.", "error");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/disclaimer", {
        maincompanyid: selectedMain,
        defaultDisclaimer: defaultDisclaimer || undefined,
        calculatorDisclaimers: calculatorDisclaimers || undefined,
        defaultPrintDisclaimer: defaultPrintDisclaimer || undefined,
        calculatorPrintDisclaimers: calculatorPrintDisclaimers || undefined,
      });
      showToast("Disclaimer saved successfully.");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Save failed.";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedMain) {
    return (
      <div className="p-4">
        <AdminPageHeader title="Manage Disclaimer" />
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          Select a main company from the header dropdown to manage its disclaimer text.
        </div>
        {toast.visible && <Toast message={toast.message} type={toast.type} visible={toast.visible} />}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <AdminPageHeader
        title="Manage Disclaimer"
        actions={
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save"}
          </button>
        }
      />

      <p className="text-sm text-gray-600">
        For <strong>{selectedCompany?.main_company_name ?? "this main company"}</strong>: configure{" "}
        <strong>footer</strong> text (calculator UI and disclaimer modal) and{" "}
        <strong>print/PDF</strong> text separately. Per-calculator overrides fall back to that channel&apos;s default;
        when print fields are empty, the API can fall back to the footer disclaimer (per server rules).
      </p>

      {loading ? (
        <div className="p-6 text-center text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* Footer / screen — default */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-blue-50 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <h2 className="text-sm font-semibold text-gray-800">Footer — default disclaimer</h2>
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-2">Shown in the calculator footer and disclaimer modal when no calculator-specific footer text is set.</p>
              <Editor
                key={`default-disclaimer-${selectedMain}`}
                value={typeof defaultDisclaimer === "string" ? defaultDisclaimer : ""}
                onEditorChange={setDefaultDisclaimer}
                apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                init={{
                  height: 220,
                  menubar: false,
                  plugins: "code link lists",
                  toolbar: "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | code",
                }}
              />
            </div>
          </div>

          {/* Footer — per calculator */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
              <Calculator className="w-4 h-4 text-gray-600" />
              <h2 className="text-sm font-semibold text-gray-800">Footer — calculator-specific</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {DISCLAIMER_CALCULATOR_KEYS.map((key) => (
                <div key={key} className="border-b border-gray-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setActiveCalc(activeCalc === key ? null : key)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-800">{CALC_LABELS[key] ?? key}</span>
                    <span className="text-xs text-gray-500">
                      {calculatorDisclaimers[key] ? "Custom" : "Uses footer default"}
                    </span>
                  </button>
                  {activeCalc === key && (
                    <div className="px-4 pb-4 pt-0">
                      <p className="text-xs text-gray-500 mb-2">Override footer for this calculator only. Leave empty to use the footer default.</p>
                      <Editor
                        key={`calc-disclaimer-${key}-${selectedMain}`}
                        value={typeof calculatorDisclaimers[key] === "string" ? calculatorDisclaimers[key] : ""}
                        onEditorChange={(val) => setCalculatorDisclaimer(key, val)}
                        apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                        init={{
                          height: 180,
                          menubar: false,
                          plugins: "code link lists",
                          toolbar: "undo redo | bold italic underline | bullist numlist | code",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Print/PDF — default */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-violet-50 flex items-center gap-2">
              <Printer className="w-4 h-4 text-violet-700" />
              <h2 className="text-sm font-semibold text-gray-800">Print / PDF — default disclaimer</h2>
            </div>
            <div className="p-4">
              <p className="text-xs text-gray-500 mb-2">
                Used for generated PDFs and print flows. If empty, your API may use the footer disclaimer until you set print-specific text.
              </p>
              <Editor
                key={`default-print-disclaimer-${selectedMain}`}
                value={typeof defaultPrintDisclaimer === "string" ? defaultPrintDisclaimer : ""}
                onEditorChange={setDefaultPrintDisclaimer}
                apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                init={{
                  height: 220,
                  menubar: false,
                  plugins: "code link lists",
                  toolbar: "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | code",
                }}
              />
            </div>
          </div>

          {/* Print/PDF — per calculator */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
              <Printer className="w-4 h-4 text-violet-600" />
              <h2 className="text-sm font-semibold text-gray-800">Print / PDF — calculator-specific</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {DISCLAIMER_CALCULATOR_KEYS.map((key) => (
                <div key={`print-${key}`} className="border-b border-gray-100 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setActivePrintCalc(activePrintCalc === key ? null : key)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-800">{CALC_LABELS[key] ?? key}</span>
                    <span className="text-xs text-gray-500">
                      {calculatorPrintDisclaimers[key] ? "Custom" : "Uses print default / API fallback"}
                    </span>
                  </button>
                  {activePrintCalc === key && (
                    <div className="px-4 pb-4 pt-0">
                      <p className="text-xs text-gray-500 mb-2">
                        Override print/PDF text for this calculator. Leave empty to use the print default (or footer fallback per API).
                      </p>
                      <Editor
                        key={`calc-print-disclaimer-${key}-${selectedMain}`}
                        value={
                          typeof calculatorPrintDisclaimers[key] === "string"
                            ? calculatorPrintDisclaimers[key]
                            : ""
                        }
                        onEditorChange={(val) => setCalculatorPrintDisclaimer(key, val)}
                        apiKey="mpwzqoajpwgkybw09orm71pipq474ewfqapm6hde2mws0a0t"
                        init={{
                          height: 180,
                          menubar: false,
                          plugins: "code link lists",
                          toolbar: "undo redo | bold italic underline | bullist numlist | code",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast.visible && <Toast message={toast.message} type={toast.type} visible={toast.visible} />}
    </div>
  );
}
